import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { extractSubdomain } from '../utils/subdomain';
import apiService from '../services/apiService';
import './AnalyticDashboard.css';

/**
 * O dashboard analítico. Dois modos, decididos pela presença de uma referência na
 * rota — `/dash/<ref>` (endereço curto escolhido por quem publica) ou o
 * `/analytic-dashboard/<slug>` de sempre, que continua valendo para não quebrar link
 * já salvo. Os dois chegam aqui e valem a mesma coisa: o apidash resolve `ref` por
 * `url_path` ou por `slug`.
 *
 *  - COM slug -> um dashboard publicado pelo MCP. O iframe recebe `src`, apontando
 *    para /api/dashboards/<slug>/html. Tem de ser `src` e não `srcDoc`: o HTML busca
 *    dados em `fetch('./data/<alias>')`, e caminho relativo precisa de um documento
 *    COM URL para resolver. Com `srcDoc` a base seria a página do painel, e todos os
 *    fetches iriam para o lugar errado — em silêncio, com o gráfico vazio.
 *
 *  - SEM slug -> o dashboard estático de sempre (POST /staticdash), que é como as
 *    empresas ainda não migradas funcionam. É também por aqui que passa a Magalu, que
 *    o App.js renderiza em tela cheia.
 */
const AnalyticDashboard = ({ user }) => {
    // `slug` vem de /analytic-dashboard/:slug; `ref` de /dash/:ref. O que chegar é a
    // referência que vai para a URL do iframe — e é por ela que o `./data/<alias>` do
    // HTML resolve, porque o caminho relativo parte de onde o documento foi servido.
    const { slug: porSlug, ref: porRef } = useParams();
    const slug = porSlug || porRef;
    const navigate = useNavigate();

    const subdomain = useMemo(() => {
        const urlSubdomain = extractSubdomain();
        if (urlSubdomain && urlSubdomain !== 'admin') return urlSubdomain;
        return user?.stores?.[0]?.subdomain || user?.stores?.[0]?.bucket_name || 'demo';
    }, [user]);

    // Ensaiar como outra pessoa: /dash/<ref>?como=supervisor ou ?como=ana.paula.
    // Vale só para quem tem preview; o servidor ignora de qualquer outro.
    const como = useMemo(
        () => new URLSearchParams(window.location.search).get('como') || null, []);

    const [html, setHtml] = useState(null);
    const [loading, setLoading] = useState(!slug);
    const [errorMsg, setErrorMsg] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const iframeRef = useRef(null);
    // Modo publicado: o iframe só monta depois de a sessão de cookie existir (ver
    // `restaurarSessaoCookie`). `restaurouNoLoad` evita laço se o 401 persistir.
    const [sessaoPronta, setSessaoPronta] = useState(false);
    const restaurouNoLoad = useRef(false);

    useEffect(() => {
        if (!slug) return;
        let cancelled = false;
        setSessaoPronta(false);
        apiService.restaurarSessaoCookie()
            .then(() => { if (!cancelled) setSessaoPronta(true); })
            .catch((err) => {
                // 401 = sessão expirada: o interceptor já está levando ao /login, e
                // montar o iframe agora só piscaria o erro em JSON antes de sair.
                if (err?.response?.status === 401) return;
                // Outra falha não deve trancar a tela: monta o iframe assim mesmo,
                // que é o comportamento antigo.
                if (!cancelled) setSessaoPronta(true);
            });
        return () => { cancelled = true; };
    }, [slug]);

    /**
     * Ao carregar o documento do iframe:
     *
     *  1. Se veio o 401 em JSON no lugar do HTML (deploy entre a restauração e o
     *     carregamento, ou um ↻ Recarregar depois de um deploy), recria a sessão e
     *     recarrega — uma vez. Se o 401 persistir, a sessão expirou: /login.
     *  2. Faz o `fetch` do dashboard levar o X-Aliado-Auth, como o axios faz. Cobre o
     *     deploy com a tela ABERTA: o restart apaga a sessão de cookie, e a próxima
     *     troca de filtro cairia em 401. O token já é legível pelo iframe (mesma
     *     origem, `allow-same-origin`), então repassá-lo não expõe nada novo.
     *     Um 401 mesmo COM o header é token expirado: a página vai para o /login, em
     *     vez de o gráfico ficar vazio ou mostrar o erro.
     */
    const aoCarregarIframe = () => {
        const frame = iframeRef.current;
        let win;
        let doc;
        try {
            win = frame?.contentWindow;
            doc = frame?.contentDocument;
        } catch (e) {
            return;
        }
        if (!win || !doc) return;

        if (doc.contentType === 'application/json') {
            let corpo = null;
            // O Chrome mostra JSON com um "Pretty-print" em volta; o texto cru fica no <pre>.
            const texto = doc.querySelector('pre')?.textContent || doc.body?.textContent || 'null';
            try { corpo = JSON.parse(texto); } catch (e) { /* não é o 401 */ }
            if (corpo?.redirect === '/login') {
                if (restaurouNoLoad.current) {
                    apiService.mandarParaLogin();
                    return;
                }
                restaurouNoLoad.current = true;
                apiService.restaurarSessaoCookie()
                    .then(() => {
                        // eslint-disable-next-line no-self-assign
                        if (iframeRef.current) iframeRef.current.src = iframeRef.current.src;
                    })
                    // 401 o interceptor já leva ao /login; qualquer outra falha também
                    // termina lá, para não deixar o JSON de erro na tela.
                    .catch((err) => {
                        if (err?.response?.status !== 401) apiService.mandarParaLogin();
                    });
            }
            return;
        }
        restaurouNoLoad.current = false;

        const fetchOriginal = win.fetch;
        if (typeof fetchOriginal !== 'function' || fetchOriginal.__aliadoAuth) return;
        const comAuth = (input, init = {}) => {
            const token = apiService.getAuthToken();
            const url = typeof input === 'string' || input instanceof win.URL ? String(input) : input?.url;
            let mesmaOrigem = false;
            try { mesmaOrigem = new win.URL(url, win.location.href).origin === win.location.origin; } catch (e) { /* url inválida */ }
            if (!mesmaOrigem) return fetchOriginal.call(win, input, init);
            const headers = new win.Headers(init.headers || (input instanceof win.Request ? input.headers : undefined));
            if (token && !headers.has('X-Aliado-Auth')) headers.set('X-Aliado-Auth', token);
            return fetchOriginal.call(win, input, { ...init, headers }).then((resp) => {
                if (resp.status === 401) apiService.mandarParaLogin();
                return resp;
            });
        };
        comAuth.__aliadoAuth = true;
        win.fetch = comAuth;
    };

    useEffect(() => {
        if (slug) return;               // modo publicado: o iframe busca sozinho
        let cancelled = false;
        setLoading(true);
        setErrorMsg(null);
        setHtml(null);

        apiService.getStaticDash(subdomain)
            .then((content) => {
                if (cancelled) return;
                setHtml(content);
            })
            .catch((err) => {
                if (cancelled) return;
                setErrorMsg(err.message || 'Erro ao carregar dashboard');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [subdomain, reloadKey, slug]);

    const recarregar = () => {
        if (slug && iframeRef.current) {
            // Recarrega o documento do iframe sem remontar o React em volta.
            // eslint-disable-next-line no-self-assign
            iframeRef.current.src = iframeRef.current.src;
            return;
        }
        setReloadKey((k) => k + 1);
    };

    return (
        <div className="analytic-dashboard-root">
            <div className="analytic-dashboard-toolbar">
                {slug && (
                    <button
                        className="ad-reload-btn"
                        onClick={() => navigate('/analytic-dashboard')}
                        title="Voltar para a lista de dashboards"
                    >
                        ← Dashboards
                    </button>
                )}
                <button
                    className="ad-reload-btn"
                    onClick={recarregar}
                    title="Recarregar dashboard"
                    disabled={loading}
                >
                    ↻ Recarregar
                </button>
            </div>

            <div className="analytic-dashboard-frame-wrap">
                {slug ? (
                    !sessaoPronta ? (
                        <div className="ad-loading">Carregando dashboard...</div>
                    ) : (
                        <iframe
                            ref={iframeRef}
                            title="Dashboard Analítico"
                            src={apiService.dashboardHtmlUrl(slug, como)}
                            onLoad={aoCarregarIframe}
                            className="analytic-dashboard-iframe"
                            /* allow-same-origin é necessário: sem ele o iframe teria origem
                               opaca e o cookie de sessão não iria no fetch para ./data/, que
                               é justamente o que autentica o proxy. A contenção real vem do
                               CSP que o servidor manda nesta resposta (connect-src 'self'),
                               não deste atributo. */
                            sandbox="allow-scripts allow-same-origin allow-popups"
                        />
                    )
                ) : (
                    <>
                        {loading && <div className="ad-loading">Carregando dashboard...</div>}
                        {!loading && errorMsg && <div className="ad-error">{errorMsg}</div>}
                        {!loading && !errorMsg && html != null && (
                            <iframe
                                ref={iframeRef}
                                title="Dashboard Analítico"
                                srcDoc={html}
                                className="analytic-dashboard-iframe"
                                sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads"
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default AnalyticDashboard;
